import { type AxiosInstance } from 'axios'

import { type ApiResponse, type ApiListResponse, isApiResponse } from '@/models/api/responses'

import { createHttpClient } from '../http/client'
import {
  setupAuthInterceptor,
  setupLoggingInterceptor,
  setupErrorInterceptor,
} from '../http/interceptors'
import { setupRetryInterceptor } from '../http/retry'

import { type ApiClient, type RequestOptions } from './types'

class ApiClientImpl implements ApiClient {
  private client: AxiosInstance

  constructor(baseURL: string) {
    this.client = createHttpClient(baseURL)

    setupAuthInterceptor(this.client)
    setupLoggingInterceptor(this.client)
    setupErrorInterceptor(this.client)
    setupRetryInterceptor(this.client)
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T | ApiResponse<T>>(url, { params })
    return this.extractData(response.data)
  }

  async post<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    const response = options?.timeoutMs
      ? await this.client.post<T | ApiResponse<T>>(url, data, { timeout: options.timeoutMs })
      : await this.client.post<T | ApiResponse<T>>(url, data)
    return this.extractData(response.data)
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T | ApiResponse<T>>(url, data)
    return this.extractData(response.data)
  }

  async delete<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.delete<T | ApiResponse<T>>(url, { data })
    return this.extractData(response.data)
  }

  async getList<T>(url: string, params?: Record<string, unknown>): Promise<T[]> {
    const response = await this.client.get<T[] | ApiListResponse<T>>(url, { params })

    if (Array.isArray(response.data)) {
      return response.data
    }

    if (response.data.error) {
      throw new Error(response.data.error)
    }

    return response.data.data
  }

  private extractData<T>(response: T | ApiResponse<T>): T {
    if (isApiResponse(response)) {
      if (response.error) {
        throw new Error(response.error)
      }
      return response.data
    }

    return response
  }
}

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

export const apiClient = new ApiClientImpl(baseURL)
